import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * Generic single-image renderer — serves conceptual-image, data-visualization
 * (chart-image kind reuses this with a subtitle hint), and any agent whose
 * result carries { image_url } / { file_url } pointing at an image.
 */
function extractImage(result: any): { url: string; caption?: string; alt?: string } | null {
  const r = result || {}
  const ad = r.agent_data || {}

  // Direct fields
  for (const src of [r, ad]) {
    if (src.image_url) return { url: src.image_url, caption: src.caption, alt: src.alt_text || src.alt }
    if (src.file_url && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(src.file_url)) {
      return { url: src.file_url, caption: src.caption }
    }
    if (Array.isArray(src.images) && src.images[0]?.url) {
      return { url: src.images[0].url, caption: src.images[0].caption }
    }
  }

  // String fallback: pick the first image-looking URL in the output text
  const text = typeof r === 'string' ? r : (r.output || '')
  if (typeof text === 'string') {
    const m = text.match(/https?:\/\/\S+\.(png|jpe?g|gif|webp|svg)/i)
    if (m) return { url: m[0] }
  }
  return null
}

export default function ImageRenderer(props: AgentOutputProps) {
  const img = useMemo(() => extractImage(props.result), [props.result])

  if (!img) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No image URL found in the agent result.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <img
        src={img.url}
        alt={img.alt || 'Agent output image'}
        className="max-w-full max-h-[560px] rounded-lg border border-border bg-muted/20 object-contain"
      />
      {img.caption && (
        <div className="text-xs text-muted-foreground italic">{img.caption}</div>
      )}
      <div className="text-xs text-muted-foreground">
        <a href={img.url} className="underline" target="_blank" rel="noreferrer">
          Open full size
        </a>
      </div>
    </div>
  )
}
